import React from "react";
import { Link } from "react-router-dom";
import { WhatsappLogo, ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { wa } from "@/lib/pfd";

const Footer = () => {
  const { t, lang } = useLang();
  const f = t.footer;

  const deskLinks = [
    { to: "/how-it-works", label: t.nav.howItWorks, tid: "footer-how-it-works" },
    { to: "/membership", label: t.nav.membership, tid: "footer-membership" },
    { to: "/about", label: t.nav.about, tid: "footer-about" },
    { to: "/journal", label: t.nav.journal, tid: "footer-journal" },
    { to: "/blog", label: "Blog", tid: "footer-blog" },
    { to: "/faq", label: "FAQ", tid: "footer-faq" },
    { to: "/contact", label: t.nav.contact, tid: "footer-contact" },
  ];

  const serviceLinks = [
    { to: "/personal-assistant-paris", label: t.services.items[0].cta, tid: "footer-private-assistant-paris" },
    { to: "/executive-assistant-paris", label: t.services.items[1].cta, tid: "footer-executive-assistant-paris" },
    { to: "/lifestyle-concierge-paris", label: t.services.items[2].cta, tid: "footer-lifestyle-concierge" },
    { to: "/travel-concierge-paris", label: t.services.items[3].cta, tid: "footer-travel-concierge" },
    { to: "/private-concierge-paris", label: "Private Concierge · Paris", tid: "footer-private-concierge" },
    { to: "/restaurant-concierge-paris", label: lang === "fr" ? "Conciergerie restaurants" : "Restaurant Concierge", tid: "footer-restaurant-concierge" },
    { to: "/personal-shopper-paris", label: "Personal Shopper · Paris", tid: "footer-personal-shopper" },
    { to: "/private-chauffeur-paris", label: lang === "fr" ? "Chauffeur privé · Paris" : "Private Chauffeur · Paris", tid: "footer-private-chauffeur" },
    { to: "/corporate-concierge-paris", label: t.nav.business, tid: "footer-business-assistance" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-stone-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-serif text-3xl sm:text-4xl text-stone-50 leading-tight">
              Book <span className="italic text-stone-400">&amp;</span> Smile
            </div>
            <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-stone-400">
              Paris · France
            </div>
            <p className="mt-5 text-stone-400 max-w-md leading-relaxed">{f.desc}</p>
            <a
              data-testid="footer-whatsapp-cta"
              href={wa(t.home.waMsgPrimary)}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#20BD5A] transition-colors"
            >
              <WhatsappLogo size={16} weight="fill" />
              +33 7 46 75 96 48
              <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow !text-stone-400">{f.servicesLabel}</p>
            <ul className="mt-5 space-y-3">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link data-testid={l.tid} to={l.to} className="inline-block py-1.5 text-stone-300 hover:text-stone-50 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-stone-400">{f.pagesLabel}</p>
            <ul className="mt-5 space-y-3">
              {deskLinks.map((l) => (
                <li key={l.to}>
                  <Link data-testid={l.tid} to={l.to} className="inline-block py-1.5 text-stone-300 hover:text-stone-50 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-stone-400">{f.hoursLabel}</p>
            <ul className="mt-5 space-y-2 text-stone-300">
              <li>{f.hours1}</li>
              <li>{f.hours2}</li>
              <li className="text-stone-400 mt-3">{f.hours3}</li>
              <li className="text-stone-400">{f.hours4}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-700 flex flex-col gap-6">
          <p data-testid="footer-legal-identity" className="text-xs sm:text-sm text-stone-400 leading-relaxed max-w-4xl">
            {f.legalIdentityLine}
          </p>
          <p data-testid="footer-legal-disclaimer" className="text-xs sm:text-sm text-stone-400 leading-relaxed max-w-4xl">
            <span className="text-stone-300 font-medium uppercase tracking-wider text-[10px] mr-2">{f.legalLabel}</span>
            {f.legalDisclaimer}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-stone-500">
              © {new Date().getFullYear()} {f.rights}
            </p>
            <div className="flex items-center gap-6">
              <Link
                data-testid="footer-legal-notice-link"
                to="/mentions-legales"
                className="inline-block py-2 text-sm text-stone-400 hover:text-stone-200 transition-colors underline underline-offset-4"
              >
                {f.legalNoticeLink}
              </Link>
              <p className="text-sm text-stone-500 italic font-serif">{f.motto}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
